import { jsxs, jsx } from 'react/jsx-runtime';
import { useResourcePermissionsByCurrentUser } from '../../../resource-detail/components/context/getDisabledActionsReasons.js';
import { useQueryResourceAncestors } from '../../../resource-detail/queries/useQueryResourceAncestors.js';
import { useQueryUserInfo } from '../../../user/queries/useQueryUserInfo.js';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter, Link } from '@fabric-app/compat';
import React__default, { useMemo } from 'react';
import { styled } from 'styled-components';
import { useQueryResourceShareTokens } from '../../queries/useQueryResourceShareTokens.js';
import { useQueryResourceMemberPermissions } from '../../queries/useQueryResourceMemberPermissions.js';
import { Modal } from '@fabric-app/ui/components/Modal/Modal';
import { Flex } from '@fabric-app/ui/components/Flex';
import { P } from '@fabric-app/ui/components/Typography';
import { framerAnimationFade } from '@fabric-app/ui/constants/framerAnimations';
import { InviteEmail } from './InviteEmail.js';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import Divider from '@fabric-app/ui/components/Divider';
import Avatar from '../../../user/components/Avatar/Avatar.js';
import { SelectPermissionRoleToken } from '../SelectPermissionRoleToken.js';
import { Button } from '@fabric-app/ui/components/Button';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import useCopyToClipboard from '@fabric-app/hooks/useCopyToClipboard';
import { generatePath } from '@fabric-app/utils/url/routing';
import AvatarUser from '../../../user/components/AvatarUser/AvatarUser.js';
import { SelectPermissionRoleMember } from '../SelectPermissionRoleMember.js';
import Tooltip from '@fabric-app/ui/components/Tooltip';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { cssTextEllipsis } from '@fabric-app/ui/constants/styled-utils';
import { appRoutes } from '../../../app/routing/routes.config.js';
import { roleDisplayValue } from '../SelectPermissionRole.js';
import { getUserColorFromId } from '@fabric-app/utils/color';
import { IconGlobe, IconMobileSheet24MiscLink } from '@fabric-app/icons/svg';
import { toast } from '../../../store/alerts.js';
import { useShowExpandedResource } from '../../../resources/stores/useExpandedResourceStore.js';
import { sortByStringKey } from '@fabric-app/utils/sort';
import { PermissionRole } from '@fabric/woody-client';

const ContentShare = ({ resource }) => {
  const resourceId = resource.id;
  const { data: user } = useQueryUserInfo();
  const router = useRouter();
  const showExpandedResource = useShowExpandedResource();
  const { permissions } = useResourcePermissionsByCurrentUser(resource);
  const { ancestors } = useQueryResourceAncestors(resourceId);
  const { hasAccessToAllAncestorsWhichAreShared, accessibleSharedAncestors } = useMemo(() => {
    const sharedThroughAncestors = ancestors.filter((item) => item.countMembers > 1 || item.countSharing > 0);
    const accessibleSharedAncestors2 = sharedThroughAncestors.filter((item) => !!item.permission);
    return {
      accessibleSharedAncestors: accessibleSharedAncestors2,
      hasAccessToAllAncestorsWhichAreShared: sharedThroughAncestors.length === accessibleSharedAncestors2.length
    };
  }, [
    ancestors
  ]);
  const { data: shareTokensData } = useQueryResourceShareTokens(resourceId);
  const { emailShareTokens, anyoneShareToken } = useMemo(() => {
    return {
      emailShareTokens: (shareTokensData || []).filter((item) => !!item.email).sort(sortByStringKey("email")),
      anyoneShareToken: (shareTokensData || [])?.find((item) => !item.email)
    };
  }, [
    shareTokensData
  ]);
  const resourceOwner = resource.user;
  const queryMemberPermissions = useQueryResourceMemberPermissions({
    resourceId
  });
  const { usersWithIndirectPermissions: usersWithIndirectPermissionsBase, directPermissions, permissionsWithUser } = queryMemberPermissions;
  const usersWithIndirectPermissions = useMemo(() => {
    return usersWithIndirectPermissionsBase.filter((item) => item.user.id !== resourceOwner?.id);
  }, [
    resourceOwner?.id,
    usersWithIndirectPermissionsBase
  ]);
  const isListOfMembersAndInvitationsVisible = (
    // owner and others
    permissionsWithUser.length > 0 || emailShareTokens.length > 0 || accessibleSharedAncestors.length > 0
  );
  return /* @__PURE__ */ jsxs(Flex, {
    direction: "column",
    gap: 24,
    style: {
      overflow: "hidden"
    },
    children: [
      /* @__PURE__ */ jsxs(Modal.Body, {
        style: {
          paddingTop: 4,
          gap: 24
        },
        children: [
          /* @__PURE__ */ jsx(InviteEmail, {
            resourceId,
            canGrantPermissions: permissions.permission.canGrantPermissions
          }),
          /* @__PURE__ */ jsx(AnyoneSharing, {
            shareToken: anyoneShareToken,
            resourceId,
            resourceKind: resource.kind,
            canGrantPermissions: permissions.permission.canGrantPermissions
          })
        ]
      }),
      isListOfMembersAndInvitationsVisible && /* @__PURE__ */ jsx(ScrollArea.Default, {
        grow: true,
        asFlex: true,
        scrollbarVariant: "subtle",
        innerDisplayFlex: true,
        children: /* @__PURE__ */ jsxs(Modal.Body, {
          style: {
            paddingTop: 0,
            gap: 16
          },
          children: [
            /* @__PURE__ */ jsx(Divider, {
              style: {
                position: "sticky",
                top: 0,
                zIndex: 10
              }
            }),
            accessibleSharedAncestors.length > 0 && /* @__PURE__ */ jsxs(P, {
              weight: 500,
              size: "xs",
              style: {
                marginBottom: "1rem"
              },
              children: [
                "Shared through",
                " ",
                accessibleSharedAncestors.map((item, index) => /* @__PURE__ */ jsxs(React__default.Fragment, {
                  children: [
                    /* @__PURE__ */ jsx(SharedThrougLink, {
                      as: Link,
                      onClick: (e) => {
                        if (router.query.listId === item.id || router.query.folderId === item.id) {
                          showExpandedResource(null);
                          e.preventDefault();
                        }
                      },
                      href: generatePath(appRoutes.folder.detail, {
                        folderId: item.id
                      }),
                      children: item.name
                    }),
                    index < accessibleSharedAncestors.length - 1 && ", "
                  ]
                }, item.id)),
                !hasAccessToAllAncestorsWhichAreShared ? " and others" : ""
              ]
            }),
            !hasAccessToAllAncestorsWhichAreShared && accessibleSharedAncestors.length === 0 && /* @__PURE__ */ jsx(P, {
              weight: 500,
              size: "xs",
              style: {
                marginBottom: "1rem"
              },
              children: "Shared through other folders."
            }),
            /* @__PURE__ */ jsx(Flex, {
              direction: "column",
              gap: "sectionsShort",
              children: /* @__PURE__ */ jsxs(AnimatePresence, {
                children: [
                  resourceOwner && /* @__PURE__ */ jsxs(InviteListItemWrapper, {
                    ...framerAnimationFade,
                    children: [
                      /* @__PURE__ */ jsxs(InviteListItemWrapperLeft, {
                        children: [
                          /* @__PURE__ */ jsx(AvatarUser, {
                            user: resourceOwner,
                            size: 24
                          }),
                          /* @__PURE__ */ jsx(InviteListTextContainer, {
                            children: /* @__PURE__ */ jsxs(InviteListTitle, {
                              children: [
                                resourceOwner.name || "Unnamed",
                                user?.id === resourceOwner.id && " (you)"
                              ]
                            })
                          })
                        ]
                      }),
                      /* @__PURE__ */ jsx(P, {
                        weight: 500,
                        size: "xs",
                        color: "placeholder",
                        children: "Owner"
                      })
                    ]
                  }),
                  directPermissions.map((permission) => {
                    const isCurrentUser = user?.id === permission.user.id;
                    return /* @__PURE__ */ jsxs(InviteListItemWrapper, {
                      ...framerAnimationFade,
                      children: [
                        /* @__PURE__ */ jsxs(InviteListItemWrapperLeft, {
                          children: [
                            /* @__PURE__ */ jsx(AvatarUser, {
                              user: permission.user,
                              size: 24
                            }),
                            /* @__PURE__ */ jsx(InviteListTextContainer, {
                              children: /* @__PURE__ */ jsxs(InviteListTitle, {
                                children: [
                                  permission.user.name || "Unnamed",
                                  isCurrentUser && " (you)"
                                ]
                              })
                            })
                          ]
                        }),
                        permission.resourceId === resourceId ? /* @__PURE__ */ jsx(SelectPermissionRoleMember, {
                          canGrantPermissions: permissions.permission.canGrantPermissions,
                          permission,
                          disabled: isCurrentUser
                        }) : /* @__PURE__ */ jsx(Tooltip, {
                          label: "Inherited permissions can't be updated",
                          children: /* @__PURE__ */ jsx(P, {
                            weight: 500,
                            size: "xs",
                            color: "placeholder",
                            children: roleDisplayValue[permission.role]
                          })
                        })
                      ]
                    }, permission.user.id);
                  }),
                  usersWithIndirectPermissions.map((userWithPerm) => {
                    const isCurrentUser = userWithPerm.user.id === user?.id;
                    const maxRoleFromAncestor = accessibleSharedAncestors.find((ancestor) => ancestor.id === userWithPerm.maxResourceRole.resourceId);
                    return /* @__PURE__ */ jsxs(InviteListItemWrapper, {
                      ...framerAnimationFade,
                      children: [
                        /* @__PURE__ */ jsxs(InviteListItemWrapperLeft, {
                          children: [
                            /* @__PURE__ */ jsx(AvatarUser, {
                              user: userWithPerm.user,
                              size: 24
                            }),
                            /* @__PURE__ */ jsx(InviteListTextContainer, {
                              children: /* @__PURE__ */ jsxs(InviteListTitle, {
                                children: [
                                  userWithPerm.user.name || "Unnamed",
                                  isCurrentUser && " (you)"
                                ]
                              })
                            })
                          ]
                        }),
                        /* @__PURE__ */ jsx(Tooltip, {
                          label: maxRoleFromAncestor ? `This permission is from "${maxRoleFromAncestor.name}"` : "This permission is inherited",
                          children: /* @__PURE__ */ jsx(P, {
                            weight: 500,
                            size: "xs",
                            color: "placeholder",
                            children: roleDisplayValue[userWithPerm.maxResourceRole.role]
                          })
                        })
                      ]
                    }, userWithPerm.user.id);
                  }),
                  emailShareTokens?.map((shareToken) => /* @__PURE__ */ jsxs(InviteListItemWrapper, {
                    ...framerAnimationFade,
                    children: [
                      /* @__PURE__ */ jsxs(InviteListItemWrapperLeft, {
                        children: [
                          /* @__PURE__ */ jsx(Avatar, {
                            color: getUserColorFromId(shareToken.id),
                            size: 24,
                            children: /* @__PURE__ */ jsx(Avatar.Text, {
                              children: shareToken.email?.charAt(0).toUpperCase()
                            })
                          }),
                          /* @__PURE__ */ jsx(InviteListTextContainer, {
                            children: /* @__PURE__ */ jsxs(InviteListTitle, {
                              children: [
                                /* @__PURE__ */ jsx("span", {
                                  children: shareToken.email
                                }),
                                " ",
                                /* @__PURE__ */ jsx(InviteStatusBadge, {
                                  children: "Pending"
                                })
                              ]
                            })
                          })
                        ]
                      }),
                      shareToken.role !== PermissionRole.OWNER && /* @__PURE__ */ jsx(SelectPermissionRoleToken, {
                        canGrantPermissions: permissions.permission.canGrantPermissions,
                        shareToken,
                        resourceId
                      })
                    ]
                  }, shareToken.id))
                ]
              })
            })
          ]
        })
      })
    ]
  });
};
const AnyoneSharing = ({ shareToken, resourceId, resourceKind, canGrantPermissions }) => {
  const [, copy] = useCopyToClipboard();
  const { isMobileView, isXSView } = useResponsive();
  return /* @__PURE__ */ jsxs(InviteListItemWrapper, {
    children: [
      /* @__PURE__ */ jsxs(Flex, {
        alignItems: "center",
        gap: 12,
        flexGrow: true,
        style: !shareToken ? {
          opacity: 0.5
        } : {},
        children: [
          /* @__PURE__ */ jsx(Avatar, {
            fixedBgColor: "#CEFFC6",
            size: 24,
            children: /* @__PURE__ */ jsx(Avatar.Text, {
              children: /* @__PURE__ */ jsx(IconGlobe, {
                style: {
                  color: "#1E3050"
                }
              })
            })
          }),
          /* @__PURE__ */ jsx(InviteListTextContainer, {
            children: /* @__PURE__ */ jsxs(InviteListTitle, {
              children: [
                "Anyone ",
                !isMobileView && "with the link"
              ]
            })
          })
        ]
      }),
      /* @__PURE__ */ jsxs(Flex, {
        gap: "element",
        children: [
          /* @__PURE__ */ jsx(SelectPermissionRoleToken, {
            shareToken,
            resourceId,
            removeLabel: isMobileView ? "disabled" : "link disabled",
            canGrantPermissions,
            "data-testid": "select-permission-role-anyone"
          }),
          /* @__PURE__ */ jsx(Button, {
            disabled: !shareToken,
            shape: isXSView ? "square" : void 0,
            onClick: () => {
              if (shareToken?.accessToken) {
                copy(resourceKind === "folder" ? generatePath(appRoutes.shared.space, {
                  token: shareToken?.accessToken
                }, {
                  withOrigin: true
                }) : generatePath(appRoutes.shared.resource, {
                  token: shareToken?.accessToken
                }, {
                  withOrigin: true
                }));
                toast({
                  content: "Link copied"
                });
              }
            },
            size: isMobileView ? "mobile-default" : "sm+",
            children: isXSView ? /* @__PURE__ */ jsx(IconMobileSheet24MiscLink, {}) : "Copy link"
          })
        ]
      })
    ]
  });
};
const InviteListItemWrapper = styled(motion.div).withConfig({
  displayName: "ContentShare__InviteListItemWrapper",
  componentId: "sc-dfdf0d57-0"
})([
  `min-height:2.1875rem;display:flex;align-items:center;gap:0.75rem;overflow:hidden;`
]);
const InviteListItemWrapperLeft = styled(Flex).attrs({
  alignItems: "center",
  gap: 12,
  flexGrow: true,
  overflow: "hidden"
}).withConfig({
  displayName: "ContentShare__InviteListItemWrapperLeft",
  componentId: "sc-dfdf0d57-1"
})([
  ``
]);
const InviteListTextContainer = styled(Flex).attrs({
  direction: "column",
  overflow: "hidden"
}).withConfig({
  displayName: "ContentShare__InviteListTextContainer",
  componentId: "sc-dfdf0d57-2"
})([
  `gap:0.1875rem;flex-grow:1;`
]);
const InviteListTitle = styled(Flex).attrs({
  alignItems: "center",
  gap: "element"
}).withConfig({
  displayName: "ContentShare__InviteListTitle",
  componentId: "sc-dfdf0d57-3"
})([
  `font-weight:500;line-height:1rem;font-size:0.8125rem;color:`,
  `;span{`,
  `;}`
], cssVar["color-text-primary"], cssTextEllipsis);
const InviteStatusBadge = styled(Flex).attrs({
  direction: "row",
  alignItems: "center",
  justifyContent: "center"
}).withConfig({
  displayName: "ContentShare__InviteStatusBadge",
  componentId: "sc-dfdf0d57-4"
})([
  `padding:0 0.1875rem;width:max-content;height:1rem;background:`,
  `;color:`,
  `;border-radius:4px;font-size:11.5px;`
], cssVar["color-bg-quarternary"], cssVar["color-text-tertiary"]);
const SharedThrougLink = styled.a.withConfig({
  displayName: "ContentShare__SharedThrougLink",
  componentId: "sc-dfdf0d57-5"
})([
  `color:`,
  `;cursor:pointer;transition:0.2s all;&:hover{color:`,
  `;}`
], cssVar["color-app-primary-text"], cssVar["color-app-primary-text-hover"]);

export { ContentShare };
//# sourceMappingURL=ContentShare.js.map
